import { BillingDashboardTile } from "../components/billing/DashboardTile";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { cachedGet } from "../lib/apiCache";
import { useAuth } from "../context/AuthContext";
import { currency, formatDate } from "../lib/utils-app";
import {
  Truck,
  AlertTriangle,
  Banknote,
  Wallet,
  TrendingUp,
  ArrowUpRight,
  Sparkles,
  Newspaper,
  Sun,
  Cloud,
  CloudRain,
  CloudDrizzle,
  CloudSnow,
  CloudFog,
  CloudLightning,
  Wind,
  Droplets,
  Undo2,
  Clock,
  ExternalLink,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ScanLine,
  Plus,
  CheckCircle2,
  UserPlus,
  CalendarDays,
  Zap,
  PartyPopper,
  Flame,
  Award,
  Wrench,
  HardHat,
  CalendarClock,
  Clock4,
  Loader2,
  MessageSquareQuote,
  ListChecks,
  Radar,
  Route,
  FileUp,
  Gauge,
  Package,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import OutstandingAlert from "../components/OutstandingAlert";
import {
  QuickAccessGrid,
  TomorrowScheduleCards,
  AnalyticsSection,
  ScannerMemoryCard,
} from "../components/DashboardWidgets";

// ──────────────────────────────────────────────────────────────────
// Dashboard (Round 52) — addresses user feedback after Round 51
//   • Greeting uses logged-in user's first name (no hardcode)
//   • Removed weekly revenue chart + most-rented items (admin-only
//     analytics section retains them)
//   • News → auto-rotating slideshow, one headline at a time
//   • Fun fact refreshes on every Refresh click (cache bust)
//   • Quick-Access grid (Vendors / Labour / Workbook / Orders)
//   • Tomorrow's Delivery + Return schedule cards with Print All
//   • Business Analytics + Scanner Memory restored (admin only)
//   • Quick actions: inline strip — no longer floating over Emergent badge
//   • Mobile-first responsive across all sections
// ──────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.mobile === "9243425500";
  // Round 125.7 — Role-based Dashboard tile visibility. `has(page)`
  // returns true when the current role's sidebar includes the given
  // section, so every card we render matches a page the user can
  // actually navigate to. Keeps managers + supervisors from staring
  // at dead links.
  const role = user?.role || "manager";
  const has = (page) => {
    if (isAdmin) return true;
    const NAV = {
      manager: new Set(["dashboard","orders","logistics","calendar","customers","photos","vendors","procurement","users"]),
      supervisor: new Set(["dashboard","orders","logistics","calendar","labour","laundry","cashPayments","inventory","users"]),
    };
    const nav = NAV[role] || NAV.manager;
    return nav.has(page);
  };

  const [summary, setSummary] = useState(null);
  const [news, setNews] = useState(null);
  const [weather, setWeather] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [funFact, setFunFact] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    setRefreshing(true);
    // Cache-bust the fun-fact so each Refresh delivers a new fact.
    const t = Date.now();
    const swr = !force;
    const tasks = [
      cachedGet("/dashboard/summary", null, { ttlMs: 30_000, force, swr }).then(setSummary).catch(() => {}),
      cachedGet("/dashboard/weather", null, { ttlMs: 300_000, force, swr }).then(setWeather).catch(() => {}),
      cachedGet("/dashboard/traffic", null, { ttlMs: 300_000, force, swr }).then(setTraffic).catch(() => {}),
      cachedGet("/dashboard/news", null, { ttlMs: 300_000, force, swr }).then(setNews).catch(() => {}),
      api.get(`/dashboard/fun-fact?t=${t}`).then((r) => setFunFact(r.data)).catch(() => {}),
    ];
    // Round 124.17 — Procurement summary now fetched by DashboardProcurementCard
    // inside QuickAccessGrid, so no need to load it here.
    await Promise.allSettled(tasks);
    setRefreshing(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-6 pb-12" data-testid="dashboard-page">
      {/* Round 125.5 — admin only; supervisors/managers must not see
          aggregate customer outstanding balances. */}
      {isAdmin && <OutstandingAlert />}

      <HeroStrip user={user} weather={weather} traffic={traffic} onRefresh={load} refreshing={refreshing} />

      <SmartBriefBanner summary={summary} />

      <PrimaryActionsHero isAdmin={isAdmin} />

      <QuickActionsStrip />

      <KpiGrid summary={summary} isAdmin={isAdmin} has={has} />

      <MyAssignedTasks user={user} />

      <TodayOperationsCard summary={summary} isAdmin={isAdmin} />

      {(isAdmin || ["manager", "accountant"].includes(user?.role) || user?.billing_access) && <BillingDashboardTile />}

      <TomorrowScheduleCards />

      {/* Round 125.7 — Quick Access grid now filters per-role inside
          the component; every user sees the cards their nav can
          reach. */}
      <QuickAccessGrid />

      {/* News (slideshow) + fun fact side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        <div className="lg:col-span-2">
          <NewsSlideshow news={news} />
        </div>
        <FunFactCard fact={funFact} onShuffle={load} refreshing={refreshing} />
      </div>

      {/* Admin-only deep insights */}
      {isAdmin && (
        <>
          <AnalyticsSection />
          <ScannerMemoryCard />
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────
// Hero strip
// ────────────────────────────────────────
function HeroStrip({ user, weather, traffic, onRefresh, refreshing }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);

  const greeting = useMemo(() => {
    const h = parseInt(
      now.toLocaleString("en-IN", { hour: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }),
      10
    );
    if (h < 5) return "Burning the midnight oil";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Good night";
  }, [now]);

  // Use the logged-in user's display name — first token only so the
  // greeting stays compact ("Adams" not "Adams Admin"). Fallback to
  // mobile prefix if name is missing.
  const displayName = (user?.name || user?.mobile || "there").trim().split(" ")[0];

  const istTime = now.toLocaleString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
  const istDate = now.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    timeZone: "Asia/Kolkata",
  });

  return (
    <section
      className="relative overflow-hidden rounded-2xl bg-slate-950 text-white p-5 sm:p-7 lg:p-9"
      data-testid="dashboard-hero"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-red-600/30 blur-3xl" />
      <div className="pointer-events-none absolute right-32 top-32 h-32 w-32 rounded-full bg-red-600/20 blur-2xl" />
      <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-8 items-start">
        <div className="lg:col-span-2 min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-red-400 font-bold">{istDate}</div>
          <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2 sm:mt-3">
            {greeting}, <span className="text-red-500" data-testid="hero-greeting-name">{displayName}</span>.
          </h1>
          <p className="mt-2 sm:mt-3 text-slate-300 text-sm sm:text-base max-w-xl">
            Adams Operations Desktop — every order, every delivery, one place.
          </p>
          <div className="mt-4 sm:mt-5 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="dashboard-refresh-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <JibbleHeroChip />
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
              <Clock className="h-3.5 w-3.5" />
              {istTime} IST
            </span>
          </div>
        </div>
        <WeatherTile weather={weather} traffic={traffic} />
      </div>
    </section>
  );
}

// ─── Jibble clock-in chip (Round 96.9) ─────────────────────────────
// Sits inside the welcome banner. Reads /api/jibble/status every 60 s
// and shows "X of Y clocked in". Clicking expands into a popover with:
//   • per-person list (In / Break / Out)
//   • big "Clock me in" CTA that opens Jibble's PWA in a new tab
// Silently hides on any auth error so ops staff without Jibble access
// don't see a broken chip.
function formatHours(h) {
  const total = Math.round((Number(h) || 0) * 60);
  const hrs = Math.floor(total / 60);
  const mins = total % 60;
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
}


function JibbleHeroChip() {
  const [status, setStatus] = useState(null);
  const [people, setPeople] = useState(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);
  const btnRef = React.useRef(null);
  const [anchor, setAnchor] = useState({ top: 80, right: 16 });

  const loadStatus = React.useCallback(() => {
    api.get("/jibble/status")
      .then((r) => { setStatus(r.data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || "unreachable"));
  }, []);

  // Round 123.2 — pause when tab is hidden.
  useEffect(() => {
    loadStatus();
    let timerId = null;
    const start = () => {
      if (timerId != null) return;
      timerId = setInterval(loadStatus, 60_000);
    };
    const stop = () => {
      if (timerId != null) { clearInterval(timerId); timerId = null; }
    };
    if (!document.hidden) start();
    const onVis = () => {
      if (document.hidden) stop();
      else { loadStatus(); start(); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadStatus]);

  useEffect(() => {
    if (!open) return;
    // Anchor the popover to the chip on desktop — since the hero
    // section has overflow-hidden, we render position:fixed to escape
    // the clip. Recompute on resize/scroll.
    const place = () => {
      const btn = btnRef.current?.getBoundingClientRect();
      if (!btn) return;
      setAnchor({
        top: Math.min(btn.bottom + 8, window.innerHeight - 200),
        right: Math.max(window.innerWidth - btn.right, 8),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api.get("/jibble/people").then((r) => setPeople(r.data.people || []))
      .catch(() => setPeople([]));
  }, [open]);

  if (err) {
    return (
      <button
        type="button"
        onClick={loadStatus}
        title={err}
        className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-100 px-3 py-1.5 rounded-full transition-colors"
        data-testid="jibble-chip-error"
      >
        <Clock4 className="h-3.5 w-3.5" />
        Jibble · setup needed
      </button>
    );
  }

  const inCount = status?.clocked_in ?? "…";
  const total = status?.total_people ?? "…";

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="jibble-hero-chip"
        className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-100 px-3 py-1.5 rounded-full transition-colors"
        title="Click for clock-in status"
      >
        <Clock4 className="h-3.5 w-3.5" />
        <span className="tabular-nums whitespace-nowrap">
          <b className="text-white">{inCount}</b>
          <span className="opacity-80"> of {total}</span>
        </span>
        <span className="hidden sm:inline">clocked in</span>
        <span className="sm:hidden">in</span>
      </button>

      {open && (
        <>
          {/* Backdrop — click anywhere to dismiss on every viewport */}
          <div
            className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent"
            onClick={() => setOpen(false)}
            data-testid="jibble-popover-overlay"
          />
          <div
            className="fixed z-50 left-2 right-2 top-16 sm:left-auto sm:right-4 sm:w-96 max-w-[calc(100vw-1rem)] bg-white text-slate-950 rounded-xl border border-slate-200 shadow-2xl overflow-hidden"
            style={window.innerWidth >= 640
              ? { top: anchor.top, right: anchor.right, left: "auto" }
              : undefined}
            data-testid="jibble-popover"
          >
            <div className="p-4 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Jibble · Live</div>
                  <div className="font-heading text-xl sm:text-2xl font-extrabold leading-tight break-words">
                    <span className="tabular-nums">{inCount} of {total}</span>{" "}
                    <span className="text-emerald-100 text-sm sm:text-base font-semibold">employees clocked in</span>
                  </div>
                  {status?.on_break > 0 && (
                    <div className="text-xs opacity-90 mt-0.5">{status.on_break} on break</div>
                  )}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-white/70 hover:text-white text-xl leading-none shrink-0"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <a
                href="https://web.jibble.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 bg-white text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-full text-xs font-heading font-extrabold shadow-sm"
                data-testid="jibble-clock-me-in"
              >
                Clock me in →
              </a>
            </div>
            <div className="max-h-[60vh] sm:max-h-72 overflow-y-auto divide-y divide-slate-100">
              {people === null ? (
                <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading roster…
                </div>
              ) : people.length === 0 ? (
                <div className="p-4 text-sm text-slate-500 italic">No employees in Jibble yet.</div>
              ) : (
                people.map((p) => (
                  <div
                    key={p.id}
                    className="px-4 py-2.5 flex items-center gap-2 text-sm"
                    data-testid="jibble-person-row"
                  >
                    <span
                      className={`h-2 w-2 rounded-full shrink-0 ${
                        p.status === "in"
                          ? "bg-emerald-500 animate-pulse"
                          : p.status === "break"
                            ? "bg-amber-500"
                            : "bg-slate-400"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-500 truncate flex items-center gap-1.5">
                        {p.role && <span>{p.role}</span>}
                        {p.role && p.hours_today > 0 && <span className="text-slate-300">·</span>}
                        {p.hours_today > 0 && (
                          <span className="tabular-nums font-semibold text-slate-700">
                            {formatHours(p.hours_today)} today
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
                        p.status === "in"
                          ? "bg-emerald-100 text-emerald-800"
                          : p.status === "break"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {p.status === "in" ? "In" : p.status === "break" ? "Break" : "Out"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Bangalore traffic ribbon — sits below the weather header inside the
// same hero glass card. Heuristic-based (rush hours + weather), so it's
// honest without needing a paid traffic feed.
function TrafficRibbon({ traffic }) {
  if (!traffic) return null;
  const colorMap = {
    emerald: { dot: "bg-emerald-400", text: "text-emerald-200" },
    amber: { dot: "bg-amber-400", text: "text-amber-200" },
    rose: { dot: "bg-rose-400", text: "text-rose-200" },
  };
  const c = colorMap[traffic.color] || colorMap.amber;
  return (
    <div
      className="mt-3 pt-3 border-t border-white/10 flex items-start gap-2"
      data-testid="dashboard-traffic-ribbon"
    >
      <span className={`h-2 w-2 rounded-full ${c.dot} mt-1.5 shrink-0 animate-pulse`} />
      <div className="min-w-0">
        <div className={`text-[10px] uppercase tracking-[0.15em] font-bold ${c.text}`}>
          Traffic · {traffic.label}
        </div>
        <div className="text-[11px] text-slate-300 leading-snug mt-0.5">{traffic.reason}</div>
      </div>
    </div>
  );
}

function weatherIcon(code) {
  if (code == null) return Sun;
  if (code === 0 || code === 1) return Sun;
  if (code === 2 || code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 51 && code <= 55) return CloudDrizzle;
  if (code >= 61 && code <= 65) return CloudRain;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 95 && code <= 99) return CloudLightning;
  return Cloud;
}

function WeatherTile({ weather, traffic }) {
  const Icon = weatherIcon(weather?.weather_code);
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4 sm:p-5 backdrop-blur-sm" data-testid="dashboard-weather-tile">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center">
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-bold">{weather?.city || "Bangalore"}</div>
          <div className="font-heading text-2xl sm:text-3xl font-extrabold mt-0.5 tabular-nums">
            {weather?.temperature_c != null ? `${Math.round(weather.temperature_c)}°` : "—"}
          </div>
        </div>
      </div>
      <div className="mt-3 text-sm font-semibold text-slate-200">{weather?.label || "Loading weather…"}</div>
      {weather && (
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400">
          {weather.humidity != null && (<span className="inline-flex items-center gap-1"><Droplets className="h-3 w-3" />{weather.humidity}%</span>)}
          {weather.wind_kmh != null && (<span className="inline-flex items-center gap-1"><Wind className="h-3 w-3" />{Math.round(weather.wind_kmh)} km/h</span>)}
        </div>
      )}
      <TrafficRibbon traffic={traffic} />
    </div>
  );
}

// ────────────────────────────────────────
// KPI grid
// ────────────────────────────────────────
function KpiGrid({ summary, isAdmin, has }) {
  const stops = summary?.schedule || {};
  const cash = summary?.cash || {};
  const outstanding = summary?.outstanding || {};
  const alerts = summary?.alerts || {};
  const items = [
    {
      label: "Today's stops",
      value: stops.today_stops_total != null ? `${stops.today_stops_assigned ?? 0}/${stops.today_stops_total}` : "—",
      sub: stops.today_stops_total > 0 ? `${stops.today_stops_assigned}/${stops.today_stops_total} assigned` : "Nothing scheduled",
      icon: Truck, tone: "from-red-500 to-red-700", to: "/logistics", testid: "kpi-today-stops",
    },
    // Round 59 — Net cash tile is admin-only (links to /finance which
    // is now gated). Hidden entirely for staff.
    ...(isAdmin
      ? [
          {
            label: "Net cash today",
            value: summary ? currency(cash.net_cash_today || 0) : "—",
            sub: summary ? `${currency(cash.receipts_today || 0)} in · ${currency(cash.expenses_today || 0)} out` : "—",
            icon: Wallet, tone: "from-emerald-500 to-emerald-700", to: "/finance", testid: "kpi-net-cash",
          },
        ]
      : []),
    // Round 125.7 — Outstanding tile shows aggregate customer ₹, so
    // it's admin-only alongside the overdue banner.
    ...(isAdmin
      ? [
          {
            label: "Outstanding",
            value: summary ? currency(outstanding.total || 0) : "—",
            sub: summary ? `${outstanding.count || 0} open ${outstanding.count === 1 ? "order" : "orders"}` : "—",
            icon: Banknote, tone: "from-amber-500 to-amber-700", to: "/orders", testid: "kpi-outstanding",
          },
        ]
      : []),
    {
      label: "Returns overdue",
      value: summary ? String(alerts.overdue_returns || 0) : "—",
      sub: alerts.overdue_returns > 0 ? "Needs pickup attention" : "All clear",
      icon: AlertTriangle,
      tone: alerts.overdue_returns > 0 ? "from-rose-500 to-rose-700" : "from-slate-500 to-slate-700",
      to: "/logistics", testid: "kpi-overdue",
    },
    // Round 125.7 — Overdue payments tile surfaces a ₹ amount, so
    // it's admin-only. Everyone else already can't see the outstanding
    // banner either.
    ...(isAdmin
      ? [
          {
            label: "Overdue payments",
            value: summary ? String(alerts.overdue_payments || 0) : "—",
            sub:
              alerts.overdue_payments > 0
                ? `${currency(alerts.overdue_payments_total || 0)} chasing`
                : "All cleared up",
            icon: Clock4,
            tone:
              alerts.overdue_payments > 0
                ? "from-rose-500 to-orange-600"
                : "from-slate-500 to-slate-700",
            to: "/orders?filter=open",
            testid: "kpi-overdue-payments",
          },
        ]
      : []),
    // Round 125.7 — Feedback due links to /customers; supervisor
    // doesn't have access to that page so we hide the tile for them.
    ...(has("customers")
      ? [
          {
            label: "Feedback due",
            value: summary ? String(alerts.feedback_due || 0) : "—",
            sub:
              alerts.feedback_due > 0
                ? "Call your top customers"
                : "No follow-ups pending",
            icon: MessageSquareQuote,
            tone:
              alerts.feedback_due > 0
                ? "from-fuchsia-500 to-purple-700"
                : "from-slate-500 to-slate-700",
            to: "/customers",
            testid: "kpi-feedback-due",
          },
        ]
      : []),
    // Round 124.17 — Procurement now lives in the full-width Quick
    // Access grid below (DashboardProcurementCard), so the small KPI
    // tile has been retired to avoid duplication.
  ];
  return (
    <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="dashboard-kpi-grid">
      {items.map((kpi) => (
        <Link key={kpi.label} to={kpi.to} data-testid={kpi.testid}
          className="group relative overflow-hidden rounded-xl bg-white border border-slate-200 p-3 sm:p-4 hover:border-slate-300 hover:shadow-sm transition-all">
          <div className="flex items-start justify-between gap-2">
            <div className={`h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br ${kpi.tone} text-white flex items-center justify-center shrink-0`}>
              <kpi.icon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <ArrowUpRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-700 transition-colors" />
          </div>
          <div className="mt-3 font-heading text-lg sm:text-2xl font-extrabold text-slate-950 tabular-nums leading-tight">{kpi.value}</div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-slate-500 font-bold mt-1 leading-tight">{kpi.label}</div>
          <div className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5 truncate">{kpi.sub}</div>
        </Link>
      ))}
    </section>
  );
}

// ────────────────────────────────────────
// Today's operations
// ────────────────────────────────────────
function TodayOperationsCard({ summary, isAdmin }) {
  const s = summary?.schedule || {};
  const total = s.today_stops_total || 0;
  const assigned = s.today_stops_assigned || 0;
  const pct = total > 0 ? Math.round((assigned / total) * 100) : 0;
  return (
    <section className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="today-ops-card">
      <header className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">Today</div>
          <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-950 mt-0.5">Operations live</h2>
        </div>
        <Link to="/logistics" data-testid="today-ops-open-link" className="text-xs font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
          Open Logistics <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </header>
      <div className="p-4 sm:p-5 space-y-4">
        <div>
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div className="text-sm text-slate-500">
              <span className="font-bold text-slate-950 tabular-nums">{assigned}</span>
              <span className="text-slate-400"> / </span>
              <span className="text-slate-700 tabular-nums">{total}</span>
              <span className="ml-1.5">stops assigned to a vehicle</span>
            </div>
            <div className={`text-xs font-bold uppercase tracking-[0.1em] ${pct === 100 ? "text-emerald-700" : pct === 0 ? "text-slate-400" : "text-amber-700"}`}>
              {total === 0 ? "Nothing today" : pct === 100 ? "Fully planned" : `${pct}% planned`}
            </div>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <OpsTile icon={Truck} label="Deliveries" today={s.deliveries_today || 0} tomorrow={s.deliveries_tomorrow || 0} tone="bg-red-50 text-red-700" />
          <OpsTile icon={Undo2} label="Returns" today={s.returns_today || 0} tomorrow={s.returns_tomorrow || 0} tone="bg-purple-50 text-purple-700" />
        </div>
        {summary && isAdmin && (
          <div className="text-xs text-slate-500 flex items-center gap-1.5 pt-2 border-t border-slate-100">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
            Today's expected revenue:{" "}
            <span className="font-bold text-slate-950">{currency(summary.expected_revenue_today || 0)}</span>
          </div>
        )}
      </div>
    </section>
  );
}

function OpsTile({ icon: Icon, label, today, tomorrow, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-2">
        <div className={`h-8 w-8 rounded-md ${tone} flex items-center justify-center`}><Icon className="h-4 w-4" /></div>
        <div className="text-xs text-slate-500 font-semibold">{label}</div>
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-950 mt-2 tabular-nums">{today}</div>
      <div className="text-[11px] text-slate-400">today · {tomorrow} tomorrow</div>
    </div>
  );
}

// ────────────────────────────────────────
// News slideshow — auto-rotates every 6s, one headline at a time
// ────────────────────────────────────────
function NewsSlideshow({ news }) {
  const items = news?.items || [];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, 6000);
    return () => clearInterval(t);
  }, [paused, items.length]);

  // Reset idx when news refreshes / count changes
  useEffect(() => {
    setIdx(0);
  }, [items.length]);

  const cur = items[idx] || null;

  return (
    <section
      className="rounded-xl bg-white border border-slate-200 overflow-hidden h-full flex flex-col"
      data-testid="news-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <header className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Newspaper className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">India · Top headlines</div>
            <h2 className="font-heading text-base sm:text-lg font-bold text-slate-950 truncate">
              Live news ticker
            </h2>
          </div>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
              data-testid="news-prev-btn"
              className="h-7 w-7 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center"
              aria-label="Previous headline"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[11px] font-mono text-slate-400 tabular-nums">{idx + 1} / {items.length}</span>
            <button
              type="button"
              onClick={() => setIdx((i) => (i + 1) % items.length)}
              data-testid="news-next-btn"
              className="h-7 w-7 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center"
              aria-label="Next headline"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </header>
      <div className="p-5 sm:p-7 flex-1 flex items-center min-h-[160px]">
        {!news ? (
          <div className="w-full text-center text-sm text-slate-400">Loading…</div>
        ) : !cur ? (
          <div className="w-full text-center text-sm text-slate-500">Couldn't load headlines just now.</div>
        ) : (
          <a
            key={idx}
            href={cur.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="news-slide"
            className="block w-full group animate-in fade-in slide-in-from-right-4 duration-500"
          >
            <div className="text-base sm:text-xl font-bold text-slate-950 group-hover:text-red-600 transition-colors leading-snug">
              {cur.title}
            </div>
            {cur.source && (
              <div className="text-xs text-slate-500 mt-3 inline-flex items-center gap-1.5">
                <span className="font-semibold">{cur.source}</span>
                <ExternalLink className="h-3 w-3 opacity-50" />
                <span className="opacity-50">Tap to read</span>
              </div>
            )}
          </a>
        )}
      </div>
      {/* Progress bar */}
      {items.length > 1 && !paused && cur && (
        <div className="h-0.5 bg-slate-100 overflow-hidden">
          <div
            key={`${idx}-bar`}
            className="h-full bg-red-600 origin-left animate-[newsbar_6s_linear]"
            style={{
              animation: "newsbar 6s linear forwards",
            }}
          />
        </div>
      )}
      <style>{`
        @keyframes newsbar {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}

// ────────────────────────────────────────
// Fun fact card (refreshes on demand via dashboard refresh)
// ────────────────────────────────────────
function FunFactCard({ fact, onShuffle, refreshing }) {
  const [revealed, setRevealed] = useState(false);
  // Reset reveal state every time the underlying fact changes
  useEffect(() => { setRevealed(false); }, [fact?.body]);

  const type = fact?.type || "fact";
  // Theme + label per type — keeps the surface visually varied so the
  // user can tell at a glance what kind of brain-food they're getting.
  const theme = {
    fact:        { grad: "from-violet-600 to-indigo-700",  badge: "Fun fact",      badgeColor: "text-amber-200" },
    india_fact:  { grad: "from-orange-500 to-rose-600",     badge: "About India",   badgeColor: "text-amber-100" },
    riddle:      { grad: "from-emerald-600 to-teal-700",    badge: "Riddle",        badgeColor: "text-amber-200" },
    puzzle:      { grad: "from-sky-600 to-blue-800",        badge: "Puzzle",        badgeColor: "text-amber-200" },
    mini_game:   { grad: "from-fuchsia-600 to-pink-700",    badge: "Mini-game",     badgeColor: "text-amber-200" },
  }[type] || { grad: "from-violet-600 to-indigo-700", badge: "Fun fact", badgeColor: "text-amber-200" };

  const hasReveal = type === "riddle" || type === "puzzle";

  return (
    <section className={`rounded-xl bg-gradient-to-br ${theme.grad} text-white p-5 sm:p-6 relative overflow-hidden h-full flex flex-col`} data-testid="fun-fact-card">
      <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className={`h-4 w-4 ${theme.badgeColor}`} />
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${theme.badgeColor}`}>{theme.badge}</div>
          </div>
          <button
            type="button"
            onClick={() => { setRevealed(false); onShuffle?.(); }}
            disabled={refreshing}
            data-testid="fun-fact-shuffle"
            className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white/15 hover:bg-white/25 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
            aria-label="Shuffle"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
            New
          </button>
        </div>
        <p className="text-sm sm:text-base font-medium leading-relaxed text-white/95 flex-1" data-testid="fun-fact-body">
          {fact?.body || fact?.fact || "Loading something interesting…"}
        </p>
        {hasReveal && fact?.answer && (
          <div className="mt-4">
            {revealed ? (
              <div className="rounded-lg bg-white/15 border border-white/20 p-3 text-sm font-semibold text-white animate-in fade-in duration-300" data-testid="fun-fact-answer">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-200 block mb-1">Answer</span>
                {fact.answer}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setRevealed(true)}
                data-testid="fun-fact-reveal"
                className="text-xs font-bold uppercase tracking-wider bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full"
              >
                Reveal answer
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}


// ──────────────────────────────────────────────────────────────────
// SmartBriefBanner — contextual one-liner that adapts to today's data.
// Pulls a single most-actionable insight (most overdue first, most
// celebratory next). Keeps the dashboard feeling alive vs. static.
// ──────────────────────────────────────────────────────────────────
function SmartBriefBanner({ summary }) {
  if (!summary) return null;
  const s = summary.schedule || {};
  const o = summary.outstanding || {};
  const a = summary.alerts || {};
  const c = summary.cash || {};
  const streaks = summary.streaks || {};
  const stopsLeft = (s.today_stops_total || 0) - (s.today_stops_assigned || 0);

  // Pick the single most relevant message — priority: blockers first,
  // then celebrations, then mild prompts.
  let msg, icon, tone;
  if (a.overdue_returns > 0) {
    msg = `${a.overdue_returns} return${a.overdue_returns === 1 ? "" : "s"} overdue — let's get them picked up.`;
    icon = AlertTriangle;
    tone = "bg-rose-50 text-rose-800 border-rose-200";
  } else if (stopsLeft > 0) {
    msg = `${stopsLeft} stop${stopsLeft === 1 ? "" : "s"} still need a vehicle for today — open Logistics to plan.`;
    icon = Truck;
    tone = "bg-amber-50 text-amber-800 border-amber-200";
  } else if (s.today_stops_total > 0) {
    msg = `Every stop today is on a vehicle — nicely planned.`;
    icon = PartyPopper;
    tone = "bg-emerald-50 text-emerald-800 border-emerald-200";
  } else if (o.count > 0) {
    msg = `${o.count} order${o.count === 1 ? "" : "s"} with ${currency(o.total)} outstanding — a few WhatsApps could clear them.`;
    icon = Banknote;
    tone = "bg-amber-50 text-amber-800 border-amber-200";
  } else if ((c.net_cash_today || 0) > 0) {
    msg = `${currency(c.net_cash_today)} net in the cash box today — keep it rolling.`;
    icon = TrendingUp;
    tone = "bg-emerald-50 text-emerald-800 border-emerald-200";
  } else {
    msg = "Nothing on fire — a great moment to scan that backlog of order forms.";
    icon = Sparkles;
    tone = "bg-slate-50 text-slate-700 border-slate-200";
  }

  const Icon = icon;
  // Streak chips — only render the ones that mean something (>= 2 days
  // counts as a streak; below that it's just "today").
  const chips = [];
  if ((streaks.returns_clean_days || 0) >= 2) {
    chips.push({
      label: `${streaks.returns_clean_days}-day returns clean`,
      flame: true,
    });
  }
  if ((streaks.payments_clean_days || 0) >= 2) {
    chips.push({
      label: `${streaks.payments_clean_days}-day payments clean`,
      flame: false,
    });
  }

  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 sm:py-3 ${tone}`}
      data-testid="smart-brief-banner"
    >
      <div className="h-7 w-7 rounded-md bg-white/60 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-sm font-semibold leading-snug flex-1 min-w-0">{msg}</div>
      {chips.length > 0 && (
        <div className="flex items-center gap-1.5 shrink-0" data-testid="streak-chips">
          {chips.map((ch) => (
            <span
              key={ch.label}
              className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white/80 border border-white/60 px-2 py-1 rounded-full"
              data-testid="streak-chip"
            >
              {ch.flame ? <Flame className="h-3 w-3 text-orange-500" /> : <Award className="h-3 w-3 text-emerald-600" />}
              <span>{ch.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// PrimaryActionsHero — 4 large tap-targets that dominate the top of the
// homepage. These are the actions ops staff actually run every day:
//   1. Scan Order       → Orders scan dialog
//   2. Mark Attendance  → Labour attendance
//   3. Assign Vehicles  → Logistics Trip Planner (auto-scrolls + opens)
//   4. Live Tracker     → Logistics Live GPS fleet view
// Deliberately larger (h-32 sm:h-36) than QuickActionsStrip so they read
// as the "front door" of the app.
// ──────────────────────────────────────────────────────────────────
function PrimaryActionsHero({ isAdmin }) {
  const { user } = useAuth();
  // Round 125.7 — Supervisor has Labour access, so "Mark Attendance"
  // is available to them alongside admin. Manager (no /labour) still
  // sees the tile hidden.
  const hasLabour = isAdmin || user?.role === "supervisor";
  const navigate = useNavigate();
  const tiles = [
    {
      icon: ScanLine,
      title: "Scan Order",
      sub: "AI · Photo or PDF",
      to: "/orders?scan=1",
      grad: "from-red-500 via-rose-600 to-red-700",
      ring: "ring-red-200",
      testid: "hero-scan-order",
    },
    {
      icon: HardHat,
      title: "Mark Attendance",
      sub: "Labour · today",
      to: "/labour",
      grad: "from-amber-500 via-orange-500 to-amber-600",
      ring: "ring-amber-200",
      testid: "hero-mark-attendance",
      gate: hasLabour,
    },
    {
      icon: Route,
      title: "Assign Vehicles",
      sub: "Trip Planner",
      to: "/logistics?tile=trip_planner",
      grad: "from-indigo-500 via-blue-600 to-indigo-700",
      ring: "ring-indigo-200",
      testid: "hero-assign-vehicles",
    },
    {
      icon: Radar,
      title: "Live Tracker",
      sub: "Fleet GPS · realtime",
      to: "/logistics?tile=live_gps",
      grad: "from-emerald-500 via-teal-600 to-emerald-700",
      ring: "ring-emerald-200",
      testid: "hero-live-tracker",
    },
  ].filter((t) => (t.gate === undefined ? true : t.gate));

  return (
    <section
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
      data-testid="primary-actions-hero"
    >
      {tiles.map((t) => (
        <button
          key={t.title}
          type="button"
          onClick={() => navigate(t.to)}
          data-testid={t.testid}
          className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${t.grad} text-white h-32 sm:h-36 p-4 sm:p-5 text-left transition-all hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98] ring-1 ${t.ring}/40 hover:ring-4`}
        >
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/15 blur-2xl group-hover:bg-white/25 transition-colors" />
          <div className="pointer-events-none absolute -left-8 -bottom-8 h-20 w-20 rounded-full bg-black/10 blur-2xl" />

          <div className="relative h-full flex flex-col justify-between">
            <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner ring-1 ring-white/30">
              <t.icon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2.25} />
            </div>
            <div>
              <div className="font-heading font-extrabold text-lg sm:text-xl leading-tight tracking-tight">
                {t.title}
              </div>
              <div className="text-[11px] sm:text-xs text-white/85 mt-0.5 font-medium tracking-wide">
                {t.sub}
              </div>
            </div>
          </div>

          {/* Arrow badge in corner */}
          <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-white/20 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowUpRight className="h-3.5 w-3.5" />
          </div>
        </button>
      ))}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────
// QuickActionsStrip — always-inline 5-button row. Vibrant but not
// floating; never overlaps the Emergent badge. Each action either
// navigates or opens the relevant page in deep-link mode.
// ──────────────────────────────────────────────────────────────────
function QuickActionsStrip() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.mobile === "9243425500";
  // Round 125.7 — Supervisor has neither /customers nor /vendors, so
  // those quick-action tiles are hidden for them. Manager still gets
  // both.
  const role = user?.role;
  const hasCustomers = isAdmin || role === "manager";
  const hasVendors = isAdmin || role === "manager";
  const actions = [
    {
      icon: FileUp,
      label: "Upload quote",
      sub: "Image or PDF → AI",
      to: "/orders?upload=quote",
      tone: "from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700",
      testid: "qa-upload-quote-btn",
    },
    // Round 59 — Finance / Labour / Workbook quick actions are admin-only.
    ...(isAdmin
      ? [
          {
            icon: Plus,
            label: "Add expense",
            sub: "Logs to today's cash",
            to: "/finance",
            tone: "from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700",
            testid: "qa-expense-btn",
          },
        ]
      : []),
    {
      icon: CheckCircle2,
      label: "Mark paid",
      sub: "Close an open order",
      to: "/orders?filter=open",
      tone: "from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800",
      testid: "qa-mark-paid-btn",
    },
    ...(hasCustomers
      ? [
          {
            icon: UserPlus,
            label: "Customers",
            sub: "Browse CRM ledger",
            to: "/customers",
            tone: "from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700",
            testid: "qa-customers-btn",
          },
        ]
      : []),
    ...(hasVendors
      ? [
          {
            icon: Wrench,
            label: "Vendor tasks",
            sub: "Pending work + dues",
            to: "/vendors",
            tone: "from-stone-500 to-zinc-700 hover:from-stone-600 hover:to-zinc-800",
            testid: "qa-vendor-btn",
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            icon: CalendarClock,
            label: "Today's appts",
            sub: "Workbook · today",
            to: "/workbook?cat=appointment&day=today",
            tone: "from-pink-500 to-fuchsia-600 hover:from-pink-600 hover:to-fuchsia-700",
            testid: "qa-appts-btn",
          },
          {
            icon: Gauge,
            label: "Km driven",
            sub: "Trip details · petrol",
            to: "/logistics?tile=trip_details",
            tone: "from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700",
            testid: "qa-km-driven-btn",
          },
        ]
      : []),
    {
      icon: CalendarDays,
      label: "Calendar",
      sub: "Week at a glance",
      to: "/calendar",
      tone: "from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700",
      testid: "qa-calendar-btn",
    },
  ];
  return (
    <section
      className="rounded-xl bg-white border border-slate-200 p-3 sm:p-4"
      data-testid="quick-actions-strip"
    >
      <div className="flex items-center gap-2 mb-3 px-1">
        <Zap className="h-4 w-4 text-amber-500" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
          Quick actions
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-2 sm:gap-3">
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            onClick={() => navigate(a.to)}
            data-testid={a.testid}
            className={`group relative overflow-hidden rounded-lg bg-gradient-to-br ${a.tone} text-white p-3 sm:p-3.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]`}
          >
            <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-white/10 blur-xl group-hover:bg-white/20 transition-colors" />
            <div className="relative flex items-center gap-2.5">
              <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-md bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                <a.icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-sm leading-tight">
                  {a.label}
                </div>
                <div className="text-[10px] sm:text-[11px] text-white/80 leading-tight mt-0.5 truncate">
                  {a.sub}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────
// MyAssignedTasks — shows Workbook tasks where `assignee` matches the
// logged-in user's name (or first-name token, case-insensitive). Always
// rendered; quietly hides when there are no matches so the layout stays
// clean for users with nothing assigned. Click a row → opens Workbook.
// ──────────────────────────────────────────────────────────────────
function MyAssignedTasks({ user }) {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.mobile === "9243425500";

  useEffect(() => {
    if (!isAdmin) return; // /workbook is admin-only (Round 59)
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/workbook?status=pending&category=task");
        if (cancelled) return;
        setItems(data || []);
      } catch {
        /* silent — non-critical widget */
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Match on full name AND first-name token (case-insensitive). e.g.
  // user "Imran Khan" matches assignee "Imran" or "imran khan".
  const myItems = useMemo(() => {
    if (!user?.name) return [];
    const fullLower = String(user.name).trim().toLowerCase();
    const firstLower = fullLower.split(/\s+/)[0] || "";
    return items.filter((it) => {
      const a = String(it.assignee || "").trim().toLowerCase();
      if (!a) return false;
      return a === fullLower || a === firstLower || a.includes(firstLower);
    });
  }, [items, user]);

  if (myItems.length === 0) return null;

  return (
    <section
      className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-100 border border-amber-200 overflow-hidden"
      data-testid="my-assigned-tasks"
    >
      <header className="p-4 sm:p-5 border-b border-amber-200/60 flex items-center gap-3">
        <div className="h-9 w-9 rounded-md bg-amber-500 text-white flex items-center justify-center shrink-0">
          <ListChecks className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
            Assigned to you
          </div>
          <h2 className="font-heading text-base sm:text-lg font-bold text-amber-950">
            {myItems.length} {myItems.length === 1 ? "task" : "tasks"} need your attention
          </h2>
        </div>
        <Link
          to="/workbook?cat=task"
          data-testid="my-tasks-open-link"
          className="text-xs font-bold text-amber-700 hover:text-amber-800 inline-flex items-center gap-0.5 shrink-0"
        >
          View all <ArrowUpRight className="h-3 w-3" />
        </Link>
      </header>
      <div className="divide-y divide-amber-200/40">
        {myItems.slice(0, 6).map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => navigate(`/workbook?cat=task`)}
            data-testid="my-task-row"
            className="w-full text-left p-3 sm:p-4 hover:bg-white/40 transition-colors flex items-center gap-3"
          >
            <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-950 truncate">{it.title}</div>
              {it.description && (
                <div className="text-xs text-amber-700/80 truncate mt-0.5">{it.description}</div>
              )}
            </div>
            {it.due_date && (
              <span className="text-xs font-bold text-amber-700 tabular-nums shrink-0">
                {formatDate(it.due_date)}
              </span>
            )}
          </button>
        ))}
      </div>
    </section>
  );
}

